<script setup>
  import { computed, onMounted, useTemplateRef, ref, watch } from 'vue'
  import { useIntersectionObserver, useElementBounding, useMediaQuery } from '@vueuse/core'
  import { useEventsStore } from '@/stores/events'

  const wrapper = useTemplateRef('wrapper')
  const stepListItems = ref([])
  const visibleItems = ref([])

  const { top } = useElementBounding(wrapper)
  const isLargeScreen = useMediaQuery('(min-width: 1024px)')

  const signetOffset = ref(isLargeScreen.value ? 40 : 20)

  const props = defineProps({
    curriculum: {
      type: Boolean,
      default: false
    },
    headline: String,
    text: String,
    items: String
  })

  onMounted(() => {
    if (props.curriculum) {
      const eventStore = useEventsStore()
      eventStore.setCurriculum(true)
    }
    stepListItems.value = wrapper.value.querySelectorAll('.step-list-item')

    stepListItems.value.forEach((item, index) => {
      if (isLargeScreen.value) {
        useIntersectionObserver(item, ([{ isIntersecting }]) => {
          if (isIntersecting) {
            visibleItems.value.push(item)
          } else {
            visibleItems.value.splice(visibleItems.value.indexOf(item), 1)
          }
        }, {
          threshold: 0.5
        })
      } else {
        item.addEventListener('click', setSignetOffset)
      }
    })
  })

  const setSignetOffset = (event) => {
    if (!event.currentTarget) return
    signetOffset.value = event.currentTarget.offsetTop
  }

  const items = computed(() => JSON.parse(props.items))

  watch(top, () => {
    if (!visibleItems.value || visibleItems.value.length < 1) return

    if (top.value < 0) {
      const lastIndex = getLastItemIndex()
      signetOffset.value = stepListItems.value[lastIndex]?.offsetTop - 20
    }
  })

  const getLastItemIndex = () => {
    const arrayLength = visibleItems.value.length
    const realIndex = visibleItems.value.findLastIndex((item, index) => arrayLength - 1 === index)

    if (!visibleItems.value[realIndex]) return 0

    return visibleItems.value[realIndex].dataset.index
  }

</script>

<template>
  <div
    class="step-list-wrapper relative"
  >
    <div
      class="step-list-line absolute top-0 lg:top-6 right-6 lg:right-auto lg:left-8 w-px h-full bg-border"
    >
      <AppIcon
        class="absolute left-1/2 -translate-x-1/2 transition-transform"
        name="Signet"
        :style="{transform: `translate(-50%, ${signetOffset}px)`}"
      />
    </div>
    <div
      ref="wrapper"
      class="step-list-items ~pt-8/14"
    >
      <slot
        name="items"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .step-list-line :deep(svg) {
    @apply ~h-6/12 ~w-6/12;
  }
  .details-tags :deep(svg) {
    @apply h-4 w-4;
  }
</style>
